import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";
// ─────────────────────────────────────────────────────────────────────
// EAGER imports — pages that are pre-rendered by react-snap (SEO homepage
// + legal pages), crawled entry points (login/subscribe) and the always-on
// shell. Keeping these eager guarantees the pre-rendered HTML hydrates
// without a "Loading…" flash and keeps first paint fast.
// ─────────────────────────────────────────────────────────────────────
import { PWAInstallBanner } from "./components/pwa";
import LandingPage from "./pages/public/LandingPage";
import AuthPage from "./pages/AuthPage";
import SubscribePage from "./pages/public/SubscribePage";
import TermosPage from "./pages/TermosPage";
import PrivacidadePage from "./pages/PrivacidadePage";
import ContactosPage from "./pages/ContactosPage";
import ConditionalChatWidget from "./components/layout/ConditionalChatWidget";

// Auth store, locale + impersonation helpers (kept eager — needed on mount)
import useAuthStore from "./store/useAuthStore";
import LocaleInitializer from "./components/common/LocaleInitializer";
import { processImpersonation } from "./utils/impersonation";

// ─────────────────────────────────────────────────────────────────────
// LAZY imports — each becomes its own JS chunk fetched on demand. The big
// admin/agency panels and every authenticated dashboard page are therefore
// NEVER part of the landing-page bundle, drastically shrinking initial load.
// ─────────────────────────────────────────────────────────────────────
const AdminPanel = lazy(() => import("./AdminPanel"));
const AgencyPanel = lazy(() => import("./AgencyPanel"));
const VisitReportPage = lazy(() => import("./pages/public/VisitReportPage"));
const LeadReplyPage = lazy(() => import("./pages/public/LeadReplyPage"));
const PropertiesPage = lazy(() => import("./pages/dashboard/PropertiesPage"));
const AppointmentsPage = lazy(() => import("./pages/dashboard/AppointmentsPage"));
const PlansPage = lazy(() => import("./pages/dashboard/PlansPage"));
const DigitalCardPage = lazy(() => import("./pages/dashboard/DigitalCardPage"));
const TagsPage = lazy(() => import("./pages/dashboard/TagsPage"));
const LeadsPage = lazy(() => import("./pages/dashboard/LeadsPage"));
const SupportPage = lazy(() => import("./pages/dashboard/SupportPage"));
const ConversationsPage = lazy(() => import("./pages/dashboard/ConversationsPage"));
const JoinAgencyPage = lazy(() => import("./pages/dashboard/JoinAgencyPage"));
const AgencyManagementPage = lazy(() => import("./pages/dashboard/AgencyManagementPage"));
const PrintRequestsPage = lazy(() => import("./pages/dashboard/PrintRequestsPage"));
const ClientTrackingPage = lazy(() => import("./pages/dashboard/ClientTrackingPage"));
const SubscribeSuccessPage = lazy(() => import("./pages/public/SubscribeSuccessPage"));
const PublicTagPage = lazy(() => import("./pages/public/PublicTagPage"));
const EmailVerificationPage = lazy(() => import("./pages/public/EmailVerificationPage"));
const PublicChatPage = lazy(() => import("./pages/public/PublicChatPage"));
const PublicBusinessCard = lazy(() => import("./pages/public/PublicBusinessCard"));
const AgentBookingPage = lazy(() => import("./pages/public/AgentBookingPage"));
const AppointmentManagePage = lazy(() => import("./pages/public/AppointmentManagePage"));
const SchedulingDashboardPage = lazy(() => import("./pages/dashboard/SchedulingDashboardPage"));
const NotificationSettingsPage = lazy(() => import("./pages/dashboard/NotificationSettingsPage"));
const GoogleCallbackPage = lazy(() => import("./pages/GoogleCallbackPage"));
const CompleteProfilePage = lazy(() => import("./pages/CompleteProfilePage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const DashboardHome = lazy(() => import("./pages/DashboardHome"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const DashboardLayout = lazy(() => import("./components/layout/DashboardLayout"));

// Lightweight fallback shown while a lazy route chunk is being fetched.
const RouteFallback = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
  </div>
);

function App() {
  const { checkAuth, isLoading, isAuthenticated } = useAuthStore();
  const [impersonationProcessed, setImpersonationProcessed] = useState(false);

  useEffect(() => {
    // One-time cleanup of legacy localStorage user tokens (XSS hardening
    // migration to HTTP-only cookies). Admin/agency tokens use different
    // keys (`admin_token`, `agency_token`) and are intentionally left.
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
    } catch (_) { /* quota / disabled storage */ }

    // Process impersonation first, before checkAuth
    const shouldReload = processImpersonation();
    if (shouldReload) {
      // Force reload to apply new token
      window.location.reload();
      return;
    }
    setImpersonationProcessed(true);
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading || !impersonationProcessed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="App">
      <LocaleInitializer>
        <BrowserRouter>
          <Toaster />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/subscribe" element={
              isAuthenticated && !new URLSearchParams(window.location.search).get('cancelled')
                ? <Navigate to="/dashboard" replace />
                : <SubscribePage />
            } />
            <Route path="/subscribe/success" element={<SubscribeSuccessPage />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/register" element={<AuthPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/auth/google" element={<GoogleCallbackPage />} />
            <Route path="/onboarding/complete-profile" element={
              isAuthenticated ? <CompleteProfilePage /> : <AuthPage />
            } />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/join/:inviteCode" element={<JoinAgencyPage />} />
            <Route path="/t/:tagId" element={<PublicTagPage />} />
            <Route path="/a/:slugOrId" element={<PublicBusinessCard />} />
            <Route path="/track/:token" element={<ClientTrackingPage />} />
            <Route path="/verify-email/:token" element={<EmailVerificationPage />} />
            <Route path="/chat/:conversationId" element={<PublicChatPage />} />
            <Route path="/visit-report/:token" element={<VisitReportPage />} />
            <Route path="/lead-reply/:leadId" element={<LeadReplyPage />} />
            
            {/* Legal Pages */}
            <Route path="/termos" element={<TermosPage />} />
            <Route path="/privacidade" element={<PrivacidadePage />} />
            <Route path="/contactos" element={<ContactosPage />} />

            {/* Public Scheduling (Calendly-like) */}
            <Route path="/agendar/manage/:token" element={<AppointmentManagePage />} />
            <Route path="/agendar/:slug" element={<AgentBookingPage />} />
            
            {/* Admin Panel */}
            <Route path="/admin/*" element={<AdminPanel />} />
            
            {/* Agency Panel */}
            <Route path="/agency/*" element={<AgencyPanel />} />
            
            {/* Redirect /app/* to /dashboard/* for backwards compatibility */}
            <Route path="/app" element={<Navigate to="/dashboard" replace />} />
            <Route path="/app/*" element={<Navigate to={window.location.pathname.replace('/app', '/dashboard') + window.location.search} replace />} />
            
            {/* ────────────────────────────────────────────────────────────
                Primary (short) dashboard routes — preferred URLs shown to
                the user. Hoisted under a single <DashboardLayout> parent
                route so the layout instance survives sub-route navigation
                (preserves in-memory state: acked/soft-dismissed urgent
                notifications, polling interval, sidebar scroll, etc.).
                ──────────────────────────────────────────────────────────── */}
            <Route element={isAuthenticated ? <DashboardLayout /> : <AuthPage />}>
              <Route path="/dashboard" element={<DashboardHome />} />
              <Route path="/properties" element={<PropertiesPage />} />
              <Route path="/scheduling" element={<SchedulingDashboardPage />} />
              <Route path="/tags" element={<TagsPage />} />
              <Route path="/card" element={<DigitalCardPage />} />
              <Route path="/leads" element={<LeadsPage />} />
              <Route path="/appointments" element={<AppointmentsPage />} />
              <Route path="/conversations" element={<ConversationsPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/plans" element={<PlansPage />} />
              <Route path="/notification-settings" element={<NotificationSettingsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/print" element={<PrintRequestsPage />} />
              <Route path="/agency" element={<AgencyManagementPage />} />
            </Route>
            {/* Short-path inline navigations */}
            <Route path="/referrals" element={<Navigate to="/profile?tab=referrals" replace />} />
            <Route path="/notifications" element={<Navigate to="/profile?tab=notifications" replace />} />
            <Route path="/settings/billing" element={<Navigate to="/plans?tab=billing" replace />} />

            {/* Legacy /dashboard/* paths — keep them as redirects so old
                bookmarks, shared links and emails keep working. */}
            <Route path="/dashboard/properties" element={<Navigate to="/properties" replace />} />
            <Route path="/dashboard/scheduling" element={<Navigate to="/scheduling" replace />} />
            <Route path="/dashboard/tags" element={<Navigate to="/tags" replace />} />
            <Route path="/dashboard/card" element={<Navigate to="/card" replace />} />
            <Route path="/dashboard/leads" element={<Navigate to="/leads" replace />} />
            <Route path="/dashboard/appointments" element={<Navigate to="/appointments" replace />} />
            <Route path="/dashboard/conversations" element={<Navigate to="/conversations" replace />} />
            <Route path="/dashboard/referrals" element={<Navigate to="/profile?tab=referrals" replace />} />
            <Route path="/dashboard/support" element={<Navigate to="/support" replace />} />
            <Route path="/dashboard/plans" element={<Navigate to="/plans" replace />} />
            <Route path="/dashboard/notifications" element={<Navigate to="/profile?tab=notifications" replace />} />
            <Route path="/dashboard/notification-settings" element={<Navigate to="/notification-settings" replace />} />
            <Route path="/dashboard/profile" element={<Navigate to="/profile" replace />} />
            <Route path="/dashboard/print" element={<Navigate to="/print" replace />} />
            <Route path="/dashboard/settings/billing" element={<Navigate to="/plans?tab=billing" replace />} />
            <Route path="/dashboard/agency" element={<Navigate to="/agency" replace />} />
          </Routes>
          </Suspense>
          <PWAInstallBanner />
          {/* AI Chat Widget - appears on public pages only (not /admin, not when authenticated) */}
          <ConditionalChatWidget isAuthenticated={isAuthenticated} />
        </BrowserRouter>
      </LocaleInitializer>
    </div>
  );
}

export default App;
